/**
 * Purchases-related dashboard widgets.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePurchaseDocuments } from '@/hooks/use-purchases';
import { useSuppliers } from '@/hooks/use-suppliers';
import { Widget, HBarList, BigStat, Sparkline, fmtMoney, isoMonthStart } from './common';
import type { TopListRow } from './common';

// ---- Monthly purchase spending ----

export function MonthlyExpensesWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading } = usePurchaseDocuments({ docType: 'invoice' });

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const lastMonthStart = isoMonthStart(now.getFullYear(), now.getMonth() - 1);
    const thisMonth = (invoices ?? []).filter((i) => i.docDate >= monthStart);
    const lastMonth = (invoices ?? []).filter(
      (i) => i.docDate >= lastMonthStart && i.docDate < monthStart,
    );
    // 30-day sparkline
    const days = 30;
    const spark: number[] = new Array(days).fill(0);
    for (let i = 0; i < days; i++) {
      const d = new Date(now.getTime() - (days - 1 - i) * 86400000).toISOString().slice(0, 10);
      for (const inv of invoices ?? []) {
        if (inv.docDate === d) spark[i] = (spark[i] ?? 0) + inv.totalTtc;
      }
    }
    return {
      thisTotal: thisMonth.reduce((s, i) => s + i.totalTtc, 0),
      lastTotal: lastMonth.reduce((s, i) => s + i.totalTtc, 0),
      count: thisMonth.length,
      spark,
    };
  }, [invoices]);

  const diff = stat.thisTotal - stat.lastTotal;
  const deltaPct = stat.lastTotal === 0 ? '' : `${diff > 0 ? '+' : ''}${((diff / stat.lastTotal) * 100).toFixed(0)}%`;

  return (
    <Widget title={t('dashboard.monthlyExpenses')} description={t('dashboard.monthlyExpensesHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.thisTotal)}
        unit="DT"
        sub={deltaPct ? `${deltaPct} ${t('dashboard.vsLastMonth')}` : t('dashboard.invoicesCount', { count: stat.count })}
        tone={diff > 0 ? 'warning' : 'neutral'}
      />
      <div className="mt-2 -mx-2">
        <Sparkline data={stat.spark} />
      </div>
    </Widget>
  );
}

// ---- Top suppliers this month ----

export function TopSuppliersWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading } = usePurchaseDocuments({ docType: 'invoice' });
  const { data: suppliers } = useSuppliers();

  const rows: TopListRow[] = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const supMap = new Map((suppliers ?? []).map((s) => [s.id, s]));
    const bySup = new Map<string, number>();
    for (const inv of invoices ?? []) {
      if (inv.docDate < monthStart) continue;
      // Purchase doc has supplierId instead of clientId
      const supId = (inv as unknown as { supplierId: string }).supplierId;
      if (!supId) continue;
      bySup.set(supId, (bySup.get(supId) ?? 0) + inv.totalTtc);
    }
    return Array.from(bySup.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([supId, total]) => {
        const s = supMap.get(supId);
        return {
          id: supId,
          label: s?.name ?? '—',
          sublabel: s?.code,
          value: total,
          unit: 'DT',
        };
      });
  }, [invoices, suppliers]);

  return (
    <Widget
      title={t('dashboard.topSuppliersThisMonth')}
      description={t('dashboard.topSuppliersHelp')}
      isLoading={isLoading}
      isEmpty={rows.length === 0}
      emptyLabel={t('dashboard.noPurchasesThisMonth')}
    >
      <HBarList rows={rows} />
    </Widget>
  );
}

// ---- Unbilled purchase receptions (BRs awaiting invoicing) ----

export function UnbilledReceptionsWidget() {
  const { t } = useTranslation();
  const { data: receptions, isLoading } = usePurchaseDocuments({ docType: 'reception' });

  const stat = useMemo(() => {
    const unbilled = (receptions ?? []).filter((r) => !r.isInvoiced);
    return {
      count: unbilled.length,
      total: unbilled.reduce((s, r) => s + r.totalTtc, 0),
    };
  }, [receptions]);

  return (
    <Widget title={t('dashboard.unbilledReceptions')} description={t('dashboard.unbilledReceptionsHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.total)}
        unit="DT"
        sub={t('dashboard.docsCount', { count: stat.count })}
        tone={stat.count > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}
